import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild, AfterViewInit, OnDestroy, NgZone } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import * as dayjs from 'dayjs';
import { CreateUserAccountRequest } from 'src/app/core/dtos/dto';
import { AffiliateLinkActivityTypeEnum, BrandEnum } from 'src/app/core/dtos/enum';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { TelInputDto } from 'src/app/core/dtos/tel-input-model';
import { AccountService } from 'src/app/core/services/account.service';
import { AffiliateService } from 'src/app/core/services/affiliate.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { SmsHelper } from 'src/app/core/services/helpers/smsHelper';
import { ModalComponent } from 'src/app/shared/components/modal/modal.component';
import { RecaptchaComponent } from 'ng-recaptcha';
import { CountryRegionService } from 'src/app/core/services/country-region.service';

declare const hcaptcha: any;

@Component({
  selector: 'app-create-account-form',
  templateUrl: './create-account-form.component.html',
  styleUrls: ['./create-account-form.component.scss']
})
export class CreateAccountFormComponent implements OnInit, OnDestroy {
  @ViewChild('confirmEmailModal') confirmEmailModal: ModalComponent;
  @ViewChild(RecaptchaComponent) reCaptchaComponent: RecaptchaComponent;
  // sign up form in proof of identity for not logged in TT
  @Input() isRegFlow: boolean;
  @Output() postRegAction = new EventEmitter<void>();

  passwordPattern = '(?=.*\\d)(?=.*[a-zA-Z]).{8,128}';
  namePattern = '[\\u0000-\\u007f]+';
  containsAtSymbolPattern = '.*\@.*';
  personalInfoAgreement: '';
  serviceTermAgreement: '';
  privacyPolicyAgreement: '';
  ageAgreement: '';
  recaptchaSiteKey: string;
  hcaptchaSiteKey: string;
  showU19Warning: boolean;
  showU18Warning: boolean;
  showNotValidAgeWarning: boolean;
  isForAffiliate: boolean;
  emailInUseMessage: string;
  userForm: FormGroup;
  today = new Date();
  minBirthDate = new Date(new Date().setFullYear(new Date().getFullYear() - 100));
  brandName: string;
  brandId: BrandEnum;
  serviceTermContent: string;
  serviceTermId: number;
  submitted: boolean;
  regPoliciesUrl: string;
  captchaResponse: string;
  shouldUseHCaptcha: boolean = false;

  get f() { return this.userForm.controls; }

  constructor(
    private formBuilder: FormBuilder,
    private accountService: AccountService,
    private toastService: HotToastService,
    private configHelper: ConfigHelper,
    private smsHelper: SmsHelper,
    private router: Router,
    private affiliateService: AffiliateService,
    private countryRegionService: CountryRegionService,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.userForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email, Validators.pattern(this.containsAtSymbolPattern)]],
      password: ['', [Validators.required, Validators.pattern(this.passwordPattern)]],
      emailConfirm: ['', Validators.required],
      passwordConfirm: ['', Validators.required],
      firstName: ['', [Validators.required, Validators.pattern(this.namePattern)]],
      middleName: ['', Validators.pattern(this.namePattern)],
      lastName: ['', [Validators.required, Validators.pattern(this.namePattern)]],
      birthDate: ['', Validators.required],
      gender: ['', Validators.required],
      captcha: ['', Validators.required],
      allowSMSNotifications: [false],
      smsPhoneNumber: [''],
      personalInfoAgreement: [],
      serviceTermAgreement: [],
      privacyPolicyAgreement: [],
      ageAgreement: []
    });
    this.brandId = this.configHelper.getBrandId();
    this.recaptchaSiteKey = this.configHelper.getRecaptchaSiteKey();
    this.hcaptchaSiteKey = this.configHelper.getHcaptchaSiteKey();
    this.brandName = this.configHelper.getBrandName();
    this.regPoliciesUrl = this.configHelper.getRegPoliciesUrl();
    this.getTermsOfService();
    this.determineCaptchaType();
  }

  private determineCaptchaType() {
    this.countryRegionService.getShouldUseHCaptcha().subscribe(
      result => {
        console.log('Should use hCaptcha:', result.content);
        this.shouldUseHCaptcha = result.content;
        if (this.shouldUseHCaptcha) {         
          this.initializeHCaptcha();
        } 
      },
      error => {
        console.error('Error checking CAPTCHA type:', error);
        // Default to reCAPTCHA on error
        this.shouldUseHCaptcha = false;
      }
    );
  }

  private initializeHCaptcha() {
    window['onCaptchaResolved'] = (response: string) => {
      this.ngZone.run(() => {
        this.onCaptchaResolved(response);
      });
    };

    window['onCaptchaError'] = () => {
      this.ngZone.run(() => {
        this.onCaptchaError();
      });
    };

    const script = document.createElement('script');
    script.src = 'https://js.hcaptcha.com/1/api.js';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);
  }

  ngOnDestroy() {
    // Clean up global callbacks when component is destroyed
    delete window['onCaptchaResolved'];
    delete window['onCaptchaError'];
  }

  onSubmit() {
    this.submitted = true;
    //iterate through the keys and set them as dirty, this will set all the form values as invalid which lets the user more easily see what is missing
    Object.keys(this.userForm.controls).forEach((control: string) => {
      this.userForm.controls[control].markAsDirty();
    });
    if (this.userForm.controls.email.value !== this.userForm.controls.emailConfirm.value) {
      this.toastService.warning('Please enter matching emails.');
      return;
    }
    if (this.userForm.controls.password.value !== this.userForm.controls.passwordConfirm.value) {
      this.toastService.warning('Please enter matching passwords.');
      return;
    }
    if (this.userForm.controls.allowSMSNotifications.value && (!this.userForm.controls.smsPhoneNumber.value ||
        this.userForm.controls.smsPhoneNumber.errors &&
       (this.userForm.controls.smsPhoneNumber.value.e164Number.length !== 12 || this.userForm.controls.smsPhoneNumber.value.e164Number.indexOf('753') !== 2))) {
      this.toastService.warning("Please enter a valid phone number.");
      return;
    }
    if (this.showNotValidAgeWarning) {
      this.toastService.warning("Please enter a valid birthdate");
      return;
    }
    //to skip the invalid phone checking
    if (this.userForm.invalid && !this.userForm.controls.allowSMSNotifications.value) {
      this.toastService.warning('Please fill in the required fields');
      return;
    }
    this.confirmEmailModal.open();
  }

  public get BrandEnum() {
    return BrandEnum;
  }
  
  getTermsOfService() {
    this.accountService.getAccountIntializationInfo().subscribe(result => {
      //remove  inline styling from serviceTerm
      this.serviceTermContent = (result.content.serviceTerm).replace(new RegExp('style="(.*?)"', 'g'), "");
      this.serviceTermId = result.content.serviceTermId;
    },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  checkEmailAndCreateAccount() {
    /*tel-input returns something in the form {
      smsPhoneNumber:{
        countryCode: "CA"
        dialCode: "+1"
        e164Number: "+16043214278"
        internationalNumber: "+1 604-321-4278"
        nationalNumber: "(604) 321-4278"
        number: "6043214278"}
      }
      so it needs to be put into a dto
    */
    let convertedTelInput = this.userForm.controls.smsPhoneNumber.value as TelInputDto;
    if (!this.userForm.controls.allowSMSNotifications.value) {
      convertedTelInput = null;
    }
    const newUser: CreateUserAccountRequest = {
      allowSMSNotifications: this.userForm.controls.allowSMSNotifications.value,
      birthDate: this.userForm.controls.birthDate.value,
      email: this.userForm.controls.email.value,
      firstName: this.userForm.controls.firstName.value,
      gender: this.userForm.controls.gender.value,
      lastName: this.userForm.controls.lastName.value,
      middleName: this.userForm.controls.middleName.value,
      password: this.userForm.controls.password.value,
      serviceTermId: this.serviceTermId,
      smsPhoneNumber: convertedTelInput ? this.smsHelper.formatPhoneNumber(convertedTelInput.e164Number) : null,
      recaptchaToken: this.captchaResponse
    };
    this.createAccount(newUser);
  }


  createAccount(request: CreateUserAccountRequest) {
    this.accountService.createUserAccount(request).subscribe(
      createResult => {
        const returnedData = JSON.parse(createResult.content);
        this.toastService.success(returnedData.message);
        if (this.affiliateService.affiliateCookieExist()) {
          this.affiliateService.recordAffiliateActivty(AffiliateLinkActivityTypeEnum.CreateAccount, returnedData.userAccountId, null).subscribe(
            (result) => { });
        }
        if (!this.isRegFlow) {
          this.router.navigate(['login']);
        }
        else {
          this.postRegAction.emit();
        }
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        } else if (error.error === 'CAPTCHA_FAILED') {
          this.toastService.error('CAPTCHA verification failed. Please try again.');
          this.reCaptchaComponent.reset();
        }
      });
  }

  sendActivationEmail(email: string) {
    this.accountService.sendActivationEmail(email)
      .subscribe(
        () => {
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage, { duration: 30000 });
            });
          }
        });
  }

  checkAge() {
    this.showU18Warning = false;
    this.showU19Warning = false;
    this.showNotValidAgeWarning = false;
    if (this.f.birthDate.valid) {
      const now = dayjs();
      const birthday = dayjs(this.f.birthDate.value);
      const age = now.diff(birthday, "year");
      if (age < 18 && this.brandId === BrandEnum.CELPIP) {
        this.showU18Warning = true;
      }
      if (age < 19) {
        this.showU19Warning = true;
      }
      const year = new Date(this.f.birthDate.value).getFullYear();
      if (!year || birthday > now || age > 100) {
        this.showNotValidAgeWarning = true;
        this.showU18Warning = false;
        this.showU19Warning = false;
      }
    }
  }

  onCaptchaResolved(response: string) {
    this.captchaResponse = response;
    this.userForm.controls.captcha.setValue(response);
  }

  onCaptchaError() {
    this.userForm.controls.captcha.setValue(null);
    this.captchaResponse = null;
    console.log('CAPTCHA error occurred');
  }

}
